import { IAuthUser } from '../state/auth/types';

export class AuthStorage {
  private readonly STORAGE_KEY = 'mora_auth_user';

  /**
   * Save authenticated user to localStorage
   */
  save(authUser: IAuthUser): boolean {
    try {
      const serializedUser = JSON.stringify(authUser);
      localStorage.setItem(this.STORAGE_KEY, serializedUser);
      return true;
    } catch (error) {
      console.error('Failed to save auth user to storage:', error);
      return false;
    }
  }

  /**
   * Load authenticated user from localStorage
   */
  load(): IAuthUser | null {
    try {
      const serializedUser = localStorage.getItem(this.STORAGE_KEY);
      if (!serializedUser) {
        return null;
      }

      // JSON.parse can throw if data is corrupted
      const authUser = JSON.parse(serializedUser);
      
      // Validate the structure of loaded data
      if (!this.isValidAuthUser(authUser)) {
        console.warn('Invalid auth user data found in storage, clearing...');
        this.clear();
        return null;
      }

      // Check if token is expired
      if (this.isTokenExpired(authUser)) {
        console.info('Expired token found in storage, clearing...');
        this.clear();
        return null;
      }

      return authUser;
    } catch (error) {
      console.error('Failed to load auth user from storage:', error);
      // Attempt to clear corrupted data (this might also fail, but we'll try)
      try {
        this.clear();
      } catch (clearError) {
        console.error('Failed to clear corrupted storage data:', clearError);
      }
      return null;
    }
  }

  /**
   * Clear authenticated user from localStorage
   */
  clear(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear auth user from storage:', error);
      return false;
    }
  }

  /**
   * Check if there's any auth data in storage
   */
  hasStoredAuth(): boolean {
    try {
      return localStorage.getItem(this.STORAGE_KEY) !== null;
    } catch (error) {
      console.error('Failed to check storage for auth data:', error);
      return false;
    }
  }

  /**
   * Validate the structure of auth user data
   */
  private isValidAuthUser(data: any): data is IAuthUser {
    return (
      data &&
      typeof data === 'object' &&
      typeof data.email === 'string' &&
      typeof data.token === 'string' &&
      typeof data.expirationDate === 'number' &&
      Array.isArray(data.roles) &&
      // userId is optional but should be string if present
      (data.userId === undefined || typeof data.userId === 'string')
    );
  }

  /**
   * Check if the token is expired
   */
  private isTokenExpired(authUser: IAuthUser): boolean {
    const now = Date.now();
    return authUser.expirationDate <= now;
  }
}

// Export singleton instance
export const authStorage = new AuthStorage();